<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  OpengroundToSpeckleForm,
  OpengroundLoginButton,
  SpeckleLoginButton,
  useOpengroundForm,
  useOpengroundStore,
  useSpeckleAuthStore
} from '@haskoning/vue-speckle'

const speckleAuthStore = useSpeckleAuthStore()
const opengroundStore = useOpengroundStore()
const { isAuthenticated } = storeToRefs(speckleAuthStore)
const { isLoggedIn, exportProgress } = storeToRefs(opengroundStore)

const isFormAvailable = computed(() => isAuthenticated.value && isLoggedIn.value)
const redirectUrl = computed(() => window.location.href)

const form = useOpengroundForm()
const isImporting = ref(false)
const alert = ref<{ type: 'success' | 'error'; message: string } | null>(null)
const isImportDisabled = computed(() => isImporting.value || !form.isComplete.value)

async function handleImport() {
  if (isImporting.value) return
  isImporting.value = true
  alert.value = null
  try {
    const result = await opengroundStore.exportFormToSpeckle(form)
    if (!result) {
      alert.value = { type: 'error', message: 'Please complete the form before importing.' }
      return
    }
    alert.value = {
      type: 'success',
      message: `Imported to Speckle model "${result.speckle_model_name}" (commit ${result.speckle_commit_id}).`
    }
  } catch (e) {
    alert.value = {
      type: 'error',
      message: e instanceof Error ? e.message : 'Import failed. Please try again.'
    }
  } finally {
    isImporting.value = false
  }
}
</script>

<template>
  <v-container fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <div class="my-3">
          <template v-if="isFormAvailable">
            <fieldset
              :disabled="isImporting"
              :class="['form-fieldset', { 'form-locked': isImporting }]"
            >
              <OpengroundToSpeckleForm :form="form" />
            </fieldset>
            <v-btn
              size="large"
              color="primary"
              class="mt-3"
              :loading="isImporting"
              :disabled="isImportDisabled"
              @click="handleImport"
            >
              Import from Openground to Speckle
            </v-btn>
            <div v-if="isImporting" class="mt-3">
              <div class="text-caption mb-1">
                {{ exportProgress.message || 'Exporting to Speckle…' }}
                <template v-if="exportProgress.percent !== null">
                  ({{ exportProgress.percent }}%)
                </template>
              </div>
              <v-progress-linear
                :model-value="exportProgress.percent ?? undefined"
                :indeterminate="exportProgress.percent === null"
                color="primary"
                height="6"
                rounded
              />
            </div>
            <v-alert
              v-if="alert"
              :type="alert.type"
              variant="tonal"
              class="mt-3"
              closable
              @click:close="alert = null"
            >
              {{ alert.message }}
            </v-alert>
          </template>
          <div v-else>
            <v-alert
              v-if="!isAuthenticated"
              variant="outlined"
              type="info"
              class="mb-3"
              color="primary"
            >
              <template #title> Sign in to Speckle to continue. </template>
              <template #text>
                <SpeckleLoginButton :redirect-url="redirectUrl" class="mt-3" />
              </template>
            </v-alert>
            <v-alert v-if="!isLoggedIn" variant="outlined" type="info" class="mb-3" color="primary">
              <template #title> Sign in to Openground to continue. </template>
              <template #text
                ><OpengroundLoginButton :redirect-url="redirectUrl" class="mt-3"
              /></template>
            </v-alert>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.form-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-inline-size: 0; /* prevent fieldset default min-width */
}
.form-locked {
  pointer-events: none;
  opacity: 0.6;
}
</style>
